import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
  Typography,
  message,
  Tooltip,
  Divider,
  Switch,
  Image
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  StopOutlined,
  DeleteOutlined,
  ReloadOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { compose } from '@reduxjs/toolkit';
import { connect } from 'react-redux';
import { injectSaga, injectReducer } from 'redux-injectors';
import { createStructuredSelector } from 'reselect';
import moment from 'moment';

import { getApiClient } from '@app/utils/apiUtils';
import { T } from '@app/components';
import saga from './saga';
import reducer, {
  getParlingoGames,
  createParlingoGame,
  updateParlingoGameStatus,
  getAvailableFixtures,
  clearAvailableFixtures,
  setPaginationParams,
  setStatusFilter,
  getParlingoDifficultyOddsRange,
  getAcceptedMarketDatalist
} from './reducer';
import {
  selectParlingoGames,
  selectIsLoading,
  selectError,
  selectAvailableFixtures,
  selectIsLoadingFixtures,
  selectCurrentPage,
  selectPageSize,
  selectTotalCount,
  selectTotalPages,
  selectCurrentStatus,
  selectGetParlingoDifficultyOddsRange,
  selectGetAcceptedMarketDatalist
} from './selectors';
import { normalizeText } from '@app/utils/uiUtils';
import { colors } from '@app/themes';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Constants for validation
const SPORTS = [
  { value: 'tennis', label: 'Tennis' },
  { value: 'baseball', label: 'Baseball' },
  { value: 'football', label: 'Football' }
];

const LEAGUES = {
  tennis: ['atp', 'wta'],
  baseball: ['mlb', 'usa_-_mlb_all_star'],
  football: ['nfl', 'ncaaf']
};

const GAME_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'default' },
  { value: 'upcoming', label: 'Upcoming', color: 'blue' },
  { value: 'active', label: 'Active', color: 'green' },
  { value: 'pending', label: 'Pending', color: 'orange' },
  { value: 'completed', label: 'Completed', color: 'purple' },
  { value: 'cancelled', label: 'Cancelled', color: 'red' }
];
const LOGO_URL = `https://bet-u-dev.s3.us-east-2.amazonaws.com/parlingo-contest-logos`;
const LOGO_SUPPORTED_LEAGUES = ['mlb', 'nfl', 'ncaaf'];
const DIFFICULTIES = ['easy', 'medium', 'hard'];
const PREVIEW_HOURS_MIN = 2;
const PREVIEW_HOURS_MAX = 240;
const DEFAULT_PRIZE_TIERS = [
  {
    tier: 1,
    tierId: 'BRONZE',
    title: 'Bronze',
    description: 'Bronze: 1 Parlay',
    amount: 250
  },
  {
    tier: 2,
    tierId: 'SILVER',
    title: 'Silver',
    description: 'Silver: 2 Parlays',
    amount: 300
  },
  {
    tier: 3,
    tierId: 'GOLD',
    title: 'Gold',
    description: 'Gold: 3+ Parlays',
    amount: 450
  }
];
const isTennis = 'tennis';
interface ParlingoContainerProps {
  games: any[];
  availableFixtures: any[];
  isLoading: boolean;
  isLoadingFixtures: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  currentStatus: string;
  difficultyOddsData: any | null;
  acceptedMarketData: any | null;
  dispatchGetParlingoGames: () => void;
  dispatchCreateParlingoGame: (data: any) => void;
  dispatchUpdateParlingoGameStatus: (id: string, status: string) => void;
  dispatchGetAvailableFixtures: (sport: string, leagues: string[]) => void;
  dispatchClearAvailableFixtures: () => void;
  dispatchSetPaginationParams: (page: number, pageSize: number) => void;
  dispatchSetStatusFilter: (status: string) => void;
  dispatchGetParlingoDifficultyOddsRange: () => void;
  dispatchGetAcceptedMarketDatalist: () => void;
}

const ParlingoContainer: React.FC<ParlingoContainerProps> = ({
  games,
  availableFixtures,
  isLoading,
  isLoadingFixtures,
  error,
  currentPage,
  pageSize,
  totalCount,
  totalPages,
  currentStatus,
  difficultyOddsData,
  acceptedMarketData,
  dispatchGetParlingoGames,
  dispatchCreateParlingoGame,
  dispatchUpdateParlingoGameStatus,
  dispatchGetAvailableFixtures,
  dispatchClearAvailableFixtures,
  dispatchSetPaginationParams,
  dispatchSetStatusFilter,
  dispatchGetParlingoDifficultyOddsRange,
  dispatchGetAcceptedMarketDatalist
}) => {
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [form] = Form.useForm();
  const contestLogoUrl = Form.useWatch('contestLogo', form);
  const [selectedSport, setSelectedSport] = useState<string>('');
  const [selectedLeagues, setSelectedLeagues] = useState<string[]>([]);
  const [selectedFixtures, setSelectedFixtures] = useState<any[]>([]);
  const [difficultyMatrix, setDifficultyMatrix] = useState<any[]>([]);
  const [prizeTiers, setPrizeTiers] = useState(DEFAULT_PRIZE_TIERS);
  const [displayedMarkets, setDisplayedMarkets] = useState<string[]>([]);
  const [difficultyThresholds, setDifficultyThresholds] = useState<
    Record<string, { min?: number | null; max?: number | null }>
  >({});
  const [thresholdErrors, setThresholdErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    console.log('ParlingoContainer: Component mounted, calling dispatchGetParlingoGames');
    dispatchGetParlingoGames();
    generateDefaultDifficultyMatrix();
    dispatchGetParlingoDifficultyOddsRange();
    dispatchGetAcceptedMarketDatalist();
  }, []);

  useEffect(() => {
    if (difficultyOddsData) {
      setDifficultyThresholds(difficultyOddsData);
    }
  }, [difficultyOddsData]);

  // Fetch available fixtures when sport and leagues change
  useEffect(() => {
    console.log('ParlingoContainer: Sport/League changed', { selectedSport, selectedLeagues });
    if (selectedSport && selectedLeagues.length > 0) {
      console.log('ParlingoContainer: Calling dispatchGetAvailableFixtures', { selectedSport, selectedLeagues });
      dispatchGetAvailableFixtures(selectedSport, selectedLeagues);
    } else {
      console.log('ParlingoContainer: Clearing available fixtures');
      dispatchClearAvailableFixtures();
      setSelectedFixtures([]);
    }
  }, [selectedSport, selectedLeagues]);

  useEffect(() => {
    if (!selectedSport || !acceptedMarketData) {
      setDisplayedMarkets([]);
      return;
    }

    const sportKey = selectedSport.toUpperCase();
    const sportMarkets = acceptedMarketData[sportKey];

    if (!sportMarkets) {
      setDisplayedMarkets([]);
      return;
    }

    let combinedMarkets = [...(sportMarkets.default || [])];

    selectedLeagues.forEach((league) => {
      if (sportMarkets.leagues && sportMarkets.leagues[league]) {
        const leagueMarkets = sportMarkets.leagues[league];
        combinedMarkets.push(...leagueMarkets);
      }
    });
    const uniqueMarkets = [...new Set(combinedMarkets)];
    setDisplayedMarkets(uniqueMarkets);
  }, [selectedSport, selectedLeagues, acceptedMarketData]);

  useEffect(() => {
    const logoLeagues = LOGO_SUPPORTED_LEAGUES;
    if (selectedLeagues.length === 1) {
      const league = selectedLeagues[0];
      if (logoLeagues.includes(league)) {
        const logoUrl = `${LOGO_URL}/${league}.png`;
        form.setFieldsValue({ contestLogo: logoUrl });
      }
    } else {
      form.setFieldsValue({ contestLogo: '' });
    }
  }, [selectedLeagues, form]);

  useEffect(() => {
    const errors = validateThresholds(difficultyThresholds);
    setThresholdErrors(errors);
  }, [difficultyThresholds]);

  // Generate default 4x4 difficulty matrix
  const generateDefaultDifficultyMatrix = () => {
    const matrix = [];
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        // Create a balanced distribution: corners easy, edges medium, center hard
        let difficulty = 'medium';
        if ((row === 0 || row === 3) && (col === 0 || col === 3)) {
          difficulty = 'easy'; // corners
        } else if ((row === 1 || row === 2) && (col === 1 || col === 2)) {
          difficulty = 'hard'; // center
        }

        matrix.push({
          row,
          column: col,
          difficulty
        });
      }
    }
    setDifficultyMatrix(matrix);
  };

  const availableLeagues = useMemo(() => (selectedSport ? LEAGUES[selectedSport] || [] : []), [selectedSport]);

  const handleSportChange = (sport: string) => {
    setSelectedSport(sport);
    setSelectedLeagues([]);
    setSelectedFixtures([]);
    form.setFieldsValue({ league: [] });
  };

  const handleLeagueChange = (leagues: string | string[]) => {
    const leagueArray = Array.isArray(leagues) ? leagues : [leagues];
    setSelectedLeagues(leagueArray);
    setSelectedFixtures([]);
    form.setFieldsValue({ league: leagueArray });
  };

  const handleFixtureSelection = (fixtureIds: string[]) => {
    const selected = availableFixtures.filter((fixture) => fixtureIds.includes(fixture.id || fixture._id));
    setSelectedFixtures(selected);
  };

  // Table columns configuration
  const columns = [
    {
      title: 'Game Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Sport',
      dataIndex: 'sport',
      key: 'sport',
      render: (sport: string) => <Tag color="blue">{sport?.toUpperCase()}</Tag>
    },
    {
      title: 'League(s)',
      dataIndex: 'league',
      key: 'league',
      render: (leagues: string[]) => (
        <Space>
          {leagues?.map((league) => (
            <Tag key={league} color="cyan">
              {league.toUpperCase()}
            </Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = GAME_STATUSES.find((s) => s.value === status);
        return <Tag color={statusConfig?.color}>{statusConfig?.label || status}</Tag>;
      }
    },
    {
      title: 'Start Time',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (time: string) => moment(time).format('MMM DD, YYYY HH:mm')
    },
    {
      title: 'Prize Pool',
      dataIndex: 'prizeTiers',
      key: 'prizeTiers',
      render: (tiers: any[], record: any) => {
        const sortedTiers = [...tiers].sort((a, b) => b.tier - a.tier);
        return (
          <Space direction="vertical" size={4}>
            {sortedTiers.map((tier) => (
              <Text key={tier.tierId}>{`${tier.title} - $${tier.amount?.$numberDecimal || '0'}`}</Text>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Entries',
      dataIndex: 'entryCount',
      key: 'entryCount',
      render: (count: number) => count || 0
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: any) => (
        <Space>
          <Tooltip title="View Details">
            <Button type="text" icon={<EyeOutlined />} onClick={() => handleViewGame(record)} />
          </Tooltip>
          {record.status === 'draft' && (
            <Tooltip title="Activate Game">
              <Button
                type="text"
                icon={<PlayCircleOutlined />}
                onClick={() => handleStatusChange(record._id, 'upcoming')}
              />
            </Tooltip>
          )}
          {(record.status === 'upcoming' || record.status === 'active') && (
            <Tooltip title="Cancel Game">
              <Button
                type="text"
                danger
                icon={<StopOutlined />}
                onClick={() => handleStatusChange(record._id, 'cancelled')}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const handleViewGame = (game: any) => {
    Modal.info({
      title: `Game Details: ${game.name}`,
      width: 800,
      content: (
        <div>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text strong>Sport:</Text> {game.sport}
            </Col>
            <Col span={12}>
              <Text strong>Status:</Text> <Tag color="blue">{game.status}</Tag>
            </Col>
            <Col span={12}>
              <Text strong>Start Time:</Text> {moment(game.startTime).format('MMM DD, YYYY HH:mm')}
            </Col>
            <Col span={12}>
              <Text strong>Prize Pool:</Text> ${Number(game.prizePool?.$numberDecimal || 0).toFixed(2)}
            </Col>
            <Col span={24}>
              <Text strong>Fixtures:</Text>
              <div style={{ marginTop: 8 }}>
                {game.fixtures?.map((fixture: any, index: number) => (
                  <Tag key={index} style={{ marginBottom: 4 }}>
                    {fixture.homeTeam?.name} vs {fixture.awayTeam?.name}
                  </Tag>
                ))}
              </div>
            </Col>
          </Row>
        </div>
      )
    });
  };

  const handleStatusChange = (gameId: string, newStatus: string) => {
    Modal.confirm({
      title: 'Confirm Status Change',
      content: `Are you sure you want to change the game status to "${newStatus}"?`,
      onOk: () => {
        dispatchUpdateParlingoGameStatus(gameId, newStatus);
      }
    });
  };

  const resetModalState = () => {
    setCreateModalVisible(false);
    form.resetFields();
    setSelectedSport('');
    setSelectedLeagues([]);
    setSelectedFixtures([]);
    setPrizeTiers(DEFAULT_PRIZE_TIERS);
    if (difficultyOddsData) {
      setDifficultyThresholds(difficultyOddsData);
    }
    setThresholdErrors({});
  };

  const handleCreateGame = async () => {
    try {
      const values = await form.validateFields();

      if (selectedFixtures.length === 0) {
        message.error('Please select at least one fixture');
        return;
      }

      if (Object.keys(thresholdErrors).length > 0) {
        message.error('Errors in the difficulty thresholds.');
        return;
      }

      // Validate prize tiers
      const totalTierAmount = prizeTiers.reduce((total, tier) => total + (tier.amount || 0), 0);
      const prizePoolAmount = values.prizePool || 0;

      if (totalTierAmount > prizePoolAmount) {
        message.error(
          `Total prize distribution ($${totalTierAmount.toFixed(
            2
          )}) cannot exceed the prize pool ($${prizePoolAmount.toFixed(2)})`
        );
        return;
      }

      // Validate that all tiers have amounts > 0
      const hasInvalidTiers = prizeTiers.some((tier) => !tier.amount || tier.amount <= 0);
      if (hasInvalidTiers) {
        message.error('All prize tiers must have amounts greater than $0.00');
        return;
      }

      // Validate that all tiers have titles and descriptions
      const hasEmptyTierFields = prizeTiers.some((tier) => !tier.title.trim() || !tier.description.trim());
      if (hasEmptyTierFields) {
        message.error('All prize tiers must have titles and descriptions');
        return;
      }

      const gameData = {
        ...values,
        fixtureIds: selectedFixtures.map((fixture) => fixture.id || fixture._id),
        difficultyMatrix,
        prizeTiers,
        thresholds: difficultyThresholds,
        winningPatterns: [{ type: 'row' }, { type: 'column' }, { type: 'diagonal' }]
      };

      dispatchCreateParlingoGame(gameData);
      resetModalState();
      message.success('Parlingo game created successfully!');
    } catch (error) {
      message.error('Please fill in all required fields');
    }
  };

  const renderDifficultyMatrix = () => (
    <div>
      <Title level={5}>Difficulty Matrix (4x4 Grid)</Title>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
        {difficultyMatrix.map((cell, index) => (
          <Select
            key={index}
            value={cell.difficulty}
            size="small"
            onChange={(value) => {
              const newMatrix = [...difficultyMatrix];
              newMatrix[index].difficulty = value;
              setDifficultyMatrix(newMatrix);
            }}
          >
            {DIFFICULTIES.map((diff) => (
              <Option key={diff} value={diff}>
                {diff.charAt(0).toUpperCase() + diff.slice(1)}
              </Option>
            ))}
          </Select>
        ))}
      </div>
      <Text type="secondary">Configure the difficulty for each position in the 4x4 bingo grid</Text>
    </div>
  );

  const handleThresholdChange = (difficulty: string, type: 'min' | 'max', value: number | null) => {
    setDifficultyThresholds((prev: any) => ({
      ...prev,
      [difficulty]: {
        ...prev[difficulty],
        [type]: value
      }
    }));
  };
  const validateThresholds = (thresholds: any) => {
    const errors: { [key: string]: string } = {};
    const ranges = Object.keys(thresholds)
      .map((key) => ({
        name: key,
        min: thresholds[key]?.min,
        max: thresholds[key]?.max
      }))
      .filter((r) => typeof r.min === 'number' && typeof r.max === 'number');

    ranges.forEach((range) => {
      if (range.min >= range.max) {
        errors[range.name] = `Min value (${range.min}) must be less than max value (${range.max}).`;
      }
    });

    if (Object.keys(errors).length > 0) {
      return errors;
    }

    const sortedRanges = [...ranges].sort((a, b) => a.min - b.min);
    for (let i = 0; i < sortedRanges.length - 1; i++) {
      const current = sortedRanges[i];
      const next = sortedRanges[i + 1];

      if (current.max >= next.min) {
        const errorMsg = `Range for ${normalizeText(current.name)} (${current.min} to ${
          current.max
        }) conflicts with range for ${normalizeText(next.name)} (${next.min} to ${next.max}).`;
        errors[current.name] = errorMsg;
        errors[next.name] = errorMsg;
      }
    }
    return errors;
  };

  const renderDifficultyThreshold = () => {
    const thresholdKeys = Object.keys(difficultyThresholds);
    if (thresholdKeys.length === 0) {
      return null;
    }

    return (
      <div>
        <Title level={5}>Difficulty Threshold</Title>
        <div style={{ marginBottom: 16 }}>
          {thresholdKeys.map((key) => (
            <Form.Item
              key={key}
              label={<Text strong>{normalizeText(key)}</Text>}
              validateStatus={thresholdErrors[key] ? 'error' : ''}
              help={thresholdErrors[key]}
              style={{ marginBottom: 8 }}
            >
              <Row gutter={16} align="middle">
                <Col span={12}>
                  <InputNumber
                    value={difficultyThresholds[key]?.min}
                    onChange={(value) => handleThresholdChange(key, 'min', value)}
                    addonBefore="Min"
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col span={12}>
                  <InputNumber
                    value={difficultyThresholds[key]?.max}
                    onChange={(value) => handleThresholdChange(key, 'max', value)}
                    addonBefore="Max"
                    style={{ width: '100%' }}
                  />
                </Col>
              </Row>
            </Form.Item>
          ))}
        </div>
      </div>
    );
  };

  const updatePrizeTier = (index: number, field: string, value: any) => {
    const newTiers = [...prizeTiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setPrizeTiers(newTiers);
  };

  const getTotalPrizeAmount = () => {
    return prizeTiers.reduce((total, tier) => total + (tier.amount || 0), 0);
  };

  useEffect(() => {
    const totalPrizeAmount = getTotalPrizeAmount();
    form.setFieldsValue({ prizePool: totalPrizeAmount });
  }, [prizeTiers]);

  const renderPrizeTiers = () => {
    return (
      <div>
        <Title level={5}>Prize Tier Configuration</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Configure the prize amounts for each tier. Players win based on how many parlays they complete.
        </Text>

        {prizeTiers.map((tier, index) => (
          <Card
            key={tier.tierId}
            size="small"
            style={{ marginBottom: 12 }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tag color={tier.tierId === 'BRONZE' ? 'orange' : tier.tierId === 'SILVER' ? 'gray' : 'gold'}>
                  {tier.title}
                </Tag>
                <Text type="secondary">Tier {tier.tier}</Text>
              </div>
            }
          >
            <Row gutter={16}>
              <Col span={8}>
                <div>
                  <Text strong>Title:</Text>
                  <Input
                    value={tier.title}
                    onChange={(e) => updatePrizeTier(index, 'title', e.target.value)}
                    placeholder="Tier title"
                    style={{ marginTop: 4 }}
                  />
                </div>
              </Col>
              <Col span={8}>
                <div>
                  <Text strong>Description:</Text>
                  <Input
                    value={tier.description}
                    onChange={(e) => updatePrizeTier(index, 'description', e.target.value)}
                    placeholder="Tier description"
                    style={{ marginTop: 4 }}
                  />
                </div>
              </Col>
              <Col span={8}>
                <div>
                  <Text strong>Prize Amount ($):</Text>
                  <InputNumber
                    value={tier.amount}
                    onChange={(value) => updatePrizeTier(index, 'amount', value || 0)}
                    min={0}
                    precision={2}
                    style={{ width: '100%', marginTop: 4 }}
                    placeholder="0.00"
                  />
                </div>
              </Col>
            </Row>
          </Card>
        ))}

        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
          <Row justify="space-between">
            <Col>
              <Text strong>Total Prize Distribution: ${getTotalPrizeAmount().toFixed(2)}</Text>
            </Col>
            {/* <Col>
              <Text type="secondary">Should match or be less than Prize Pool above</Text>
            </Col> */}
          </Row>
        </div>
      </div>
    );
  };

  const renderAcceptedMarketsPreview = () => {
    return (
      <Card size="small" style={{ backgroundColor: '#fafafa' }}>
        <Title level={5} style={{ marginTop: 0 }}>
          <strong>Accepted Markets</strong>
        </Title>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {displayedMarkets.map((market) => (
            <Tag key={market} color="default">
              {market}
            </Tag>
          ))}
        </div>
      </Card>
    );
  };

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <T id="parlingo_games" />
            </Title>
          </Col>
          <Col>
            <Space>
              <Select
                value={currentStatus}
                style={{ width: 120 }}
                onChange={(value) => {
                  dispatchSetStatusFilter(value);
                  dispatchGetParlingoGames();
                }}
              >
                <Option value="all">All Status</Option>
                <Option value="draft">Draft</Option>
                <Option value="upcoming">Upcoming</Option>
                <Option value="active">Active</Option>
                <Option value="pending">Pending</Option>
                <Option value="completed">Completed</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
              <Button icon={<ReloadOutlined />} onClick={dispatchGetParlingoGames} loading={isLoading}>
                Refresh
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
                Create Game
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <Table
        columns={columns}
        dataSource={games}
        loading={isLoading}
        rowKey="_id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          onChange: (page, size) => {
            dispatchSetPaginationParams(page, size);
            dispatchGetParlingoGames();
          },
          onShowSizeChange: (current, size) => {
            dispatchSetPaginationParams(current, size);
            dispatchGetParlingoGames();
          }
        }}
      />

      {/* Create Game Modal */}
      <Modal
        title="Create Parlingo Game"
        open={createModalVisible}
        onCancel={resetModalState}
        width={800}
        footer={[
          <Button key="cancel" onClick={resetModalState}>
            Cancel
          </Button>,
          <Button key="create" type="primary" onClick={handleCreateGame}>
            Create Game
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            previewHoursBeforeStart: 240,
            maxShuffles: 10,
            phantomEntryCount: 0
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Game Name" rules={[{ required: true, message: 'Please enter game name' }]}>
                <Input placeholder="Enter game name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sport" label="Sport" rules={[{ required: true, message: 'Please select sport' }]}>
                <Select placeholder="Select sport" onChange={handleSportChange}>
                  {SPORTS.map((sport) => (
                    <Option key={sport.value} value={sport.value}>
                      {sport.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="league"
                label="League(s)"
                rules={[{ required: true, message: 'Please select league(s)' }]}
              >
                <Select
                  mode={selectedSport === isTennis ? 'multiple' : undefined}
                  placeholder="Select leagues"
                  disabled={!selectedSport}
                  onChange={handleLeagueChange}
                >
                  {availableLeagues.map((league) => (
                    <Option key={league} value={league}>
                      {league.toUpperCase()}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phantomEntryCount" label="Phantom Entry Count">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Available Games"
            help={
              selectedFixtures.length > 0
                ? `${selectedFixtures.length} game(s) selected`
                : 'Select sport and league(s) first to see available games'
            }
          >
            <Select
              mode="multiple"
              placeholder="Select games for the parlingo"
              loading={isLoadingFixtures}
              disabled={!selectedSport || selectedLeagues.length === 0}
              onChange={handleFixtureSelection}
              value={selectedFixtures.map((fixture) => fixture.id || fixture._id)}
              style={{ width: '100%' }}
              maxTagCount={3}
              maxTagTextLength={30}
            >
              {availableFixtures.length > 1
                ? availableFixtures.map((fixture) => {
                    console.log(fixture?.home_competitors[0]?.name);
                    return (
                      <Option
                        key={fixture.id || fixture._id}
                        value={fixture.id || fixture._id}
                        title={`${fixture.home_competitors?.[0]?.name || fixture.homeTeam?.name || fixture.home} vs ${
                          fixture.away_competitors?.[0]?.name || fixture.awayTeam?.name || fixture.away
                        }`}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold' }}>
                            {fixture.home_competitors?.[0]?.name || fixture.homeTeam?.name || fixture.home} vs{' '}
                            {fixture.away_competitors?.[0]?.name || fixture.awayTeam?.name || fixture.away}
                          </span>
                          <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                            {fixture.start_date ? moment(fixture.start_date).format('MMM DD, YYYY HH:mm') : ''}
                          </span>
                        </div>
                      </Option>
                    );
                  })
                : null}
            </Select>
            {selectedFixtures.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary">Selected games:</Text>
                <div style={{ marginTop: '4px' }}>
                  {selectedFixtures.map((fixture) => (
                    <Tag key={fixture.id || fixture._id} style={{ marginBottom: '4px' }} color="blue">
                      {fixture.home_competitors?.[0]?.name || fixture.homeTeam?.name || fixture.home} vs{' '}
                      {fixture.away_competitors?.[0]?.name || fixture.awayTeam?.name || fixture.away}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </Form.Item>

          <Row gutter={16} align="bottom">
            <Col span={8}>
              <Form.Item
                name="previewHoursBeforeStart"
                label={
                  <div
                    style={{
                      display: 'flex',
                      marginTop: 16,
                      width: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Row>
                      <span>Accessible From</span>
                      <span>
                        (<strong>hours</strong> before start time)
                      </span>
                    </Row>

                    <Tooltip
                      title={
                        <>
                          <div>
                            Number of hours before the contest start time when users can begin accessing and
                            participating.
                          </div>
                          <div>
                            Example: If the contest starts Monday at 9:00 AM and this value is 24, users will have
                            access starting Sunday at 9:00 AM.
                          </div>
                          <div>
                            Before this time, the contest will be in <strong>preview</strong> mode.
                          </div>
                        </>
                      }
                      overlayStyle={{ maxWidth: '420px' }}
                      overlayInnerStyle={{
                        backgroundColor: colors.greyBackground,
                        color: colors.neutral90
                      }}
                      color={colors.greyBackground}
                    >
                      <InfoCircleOutlined style={{ color: colors.neutral90, cursor: 'help' }} />
                    </Tooltip>
                  </div>
                }
              >
                <InputNumber min={PREVIEW_HOURS_MIN} max={PREVIEW_HOURS_MAX} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxShuffles" label="Max Shuffles">
                <InputNumber min={5} max={50} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="prizePool" label="Prize Pool ($)">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} disabled readOnly />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="subHeader" label="Sub Header">
            <Input placeholder="Optional sub header" />
          </Form.Item>
          <Form.Item name="contestLogo" label="Contest Logo">
            <Input
              placeholder="Contest Logo URL"
              addonAfter={contestLogoUrl ? <Image src={contestLogoUrl} width={28} height={28} /> : null}
            />
          </Form.Item>

          {selectedSport && acceptedMarketData && (
            <>
              <Divider />
              {renderAcceptedMarketsPreview()}
            </>
          )}

          <Divider />
          {renderDifficultyThreshold()}
          {renderDifficultyMatrix()}

          <Divider />
          {renderPrizeTiers()}
        </Form>
      </Modal>
    </Card>
  );
};

const mapStateToProps = createStructuredSelector({
  games: selectParlingoGames(),
  availableFixtures: selectAvailableFixtures(),
  isLoading: selectIsLoading(),
  isLoadingFixtures: selectIsLoadingFixtures(),
  error: selectError(),
  currentPage: selectCurrentPage(),
  pageSize: selectPageSize(),
  totalCount: selectTotalCount(),
  totalPages: selectTotalPages(),
  currentStatus: selectCurrentStatus(),
  difficultyOddsData: selectGetParlingoDifficultyOddsRange(),
  acceptedMarketData: selectGetAcceptedMarketDatalist()
});

export function mapDispatchToProps(dispatch: any) {
  return {
    dispatchGetParlingoGames: () => dispatch(getParlingoGames()),
    dispatchCreateParlingoGame: (data: any) => dispatch(createParlingoGame(data)),
    dispatchUpdateParlingoGameStatus: (id: string, status: string) =>
      dispatch(updateParlingoGameStatus({ id, status })),
    dispatchGetAvailableFixtures: (sport: string, leagues: string[]) =>
      dispatch(getAvailableFixtures({ sport, leagues })),
    dispatchClearAvailableFixtures: () => dispatch(clearAvailableFixtures()),
    dispatchSetPaginationParams: (page: number, pageSize: number) => dispatch(setPaginationParams({ page, pageSize })),
    dispatchSetStatusFilter: (status: string) => dispatch(setStatusFilter(status)),
    dispatchGetParlingoDifficultyOddsRange: () => dispatch(getParlingoDifficultyOddsRange()),
    dispatchGetAcceptedMarketDatalist: () => dispatch(getAcceptedMarketDatalist())
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(
  withConnect,
  injectReducer({ key: 'parlingoContainer', reducer }),
  injectSaga({ key: 'parlingoContainer', saga })
)(ParlingoContainer);
